import React, {
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import { baseUrl } from "../config/config";
import io from "socket.io-client";
import { DEFAULT_LANGUAGE, LANG_KEY, resolveBaseLanguage } from "../i18n";

const CreateContentSetting = React.createContext();

const noopSocket = {
  connected: false,
  on: () => {},
  off: () => {},
  emit: () => {},
  disconnect: () => {},
  once: () => {},
  removeAllListeners: () => {},
};

export const useContentSetting = () => useContext(CreateContentSetting);

export function ContentSettingState({ children }) {
  const [userInfo, setUserInfo] = useState({});
  const [startedMeeting, setStartedMeeting] = useState({});
  const [initSettings, setInitSettings] = useState({});
  const [unreadMessageCount, setUnreadMessageCount] = useState(0);
  const [studentsGroup, setStudentsGroup] = useState([]);
  const [conversation, setConversation] = useState("");
  const [socket, setSocket] = useState(noopSocket);

  const socketRef = useRef(noopSocket);
  const socketUserIdRef = useRef(null);

  const assignSocket = useCallback((next, userId = null) => {
    socketRef.current = next;
    socketUserIdRef.current = userId;
    setSocket(next);
  }, []);

  const disconnectSocketSync = useCallback(() => {
    const current = socketRef.current;
    if (
      !current ||
      current === noopSocket ||
      typeof current.disconnect !== "function"
    ) {
      assignSocket(noopSocket, null);
      return;
    }
    try {
      if (typeof current.removeAllListeners === "function") {
        current.removeAllListeners();
      }
      current.disconnect();
    } catch {
      /* ignore */
    }
    assignSocket(noopSocket, null);
  }, [assignSocket]);

  const START_SOCKET_FORCEFULLY = useCallback(
    (user_id) => {
      if (!user_id) return;
      const current = socketRef.current;
      if (
        socketUserIdRef.current === user_id &&
        current !== noopSocket &&
        current.connected
      ) {
        return;
      }
      disconnectSocketSync();
      const language = resolveBaseLanguage(
        localStorage.getItem(LANG_KEY) || DEFAULT_LANGUAGE,
      );
      const next = io(`${baseUrl}?user_id=${user_id}&lang=${language}`, {
        withCredentials: true,
        transports: ["websocket", "polling"],
        extraHeaders: {
          "Accept-Language": language,
          "X-Language": language,
        },
      });
      assignSocket(next, user_id);
    },
    [assignSocket, disconnectSocketSync],
  );

  const STOP_SOCKET_FORCEFULLY = useCallback(() => {
    return new Promise((resolve) => {
      const current = socketRef.current;
      if (
        !current ||
        current === noopSocket ||
        typeof current.disconnect !== "function"
      ) {
        assignSocket(noopSocket, null);
        resolve();
        return;
      }
      if (!current.connected) {
        disconnectSocketSync();
        resolve();
        return;
      }
      let settled = false;
      const finish = () => {
        if (settled) return;
        settled = true;
        assignSocket(noopSocket, null);
        resolve();
      };
      const fallback = setTimeout(finish, 4000);
      current.once("disconnect", () => {
        clearTimeout(fallback);
        finish();
      });
      try {
        current.disconnect();
      } catch {
        clearTimeout(fallback);
        finish();
      }
    });
  }, [assignSocket, disconnectSocketSync]);

  const collection = useMemo(
    () => ({
      userInfo,
      socket,
      unreadMessageCount,
      startedMeeting,
      setUserInfo,
      setStartedMeeting,
      setUnreadMessageCount,
      START_SOCKET_FORCEFULLY,
      STOP_SOCKET_FORCEFULLY,
      setInitSettings,
      initSettings,
      studentsGroup,
      setStudentsGroup,
      conversation,
      setConversation,
    }),
    [
      userInfo,
      socket,
      unreadMessageCount,
      startedMeeting,
      START_SOCKET_FORCEFULLY,
      STOP_SOCKET_FORCEFULLY,
      initSettings,
      studentsGroup,
      conversation,
    ],
  );

  return (
    <CreateContentSetting.Provider value={collection}>
      {children}
    </CreateContentSetting.Provider>
  );
}
